/* React base class */
import { Component } from "react"

/* QR code for the app install link */
import { QRCodeSVG } from "qrcode.react"

/* SEO helper — title, description, canonical, Open Graph and JSON-LD */
import { applySeo } from "../../helpers"

/* Icon component */
import Icon from "../../components/Icon"

/* Lead API: the callback request of the landing */
import { partner } from "../../api/Lead"

/* Pro web version — the browser workspace for masters and administrators */
const PRO_WEB = "https://pro.bookify.kz"

/* Store links of the Bookify Pro mobile app */
const APP_STORE = "https://apps.apple.com/app/apple-store/id6742787073"
const GOOGLE_PLAY = "https://play.google.com/store/apps/details?id=kz.bookify.pro"
const INSTALL = "https://i.bookify.kz/bookify-pro"

/* Sales contact — the WhatsApp chat of the team */
const WHATSAPP = "https://wa.me/77473125458"

/* Header and drawer navigation — every id below exists as a section anchor */
const NAVIGATION = [
    { id: "features", label: "Возможности" },
    { id: "pricing", label: "Тарифы" },
    { id: "faq", label: "Вопросы" },
    { id: "contacts", label: "Контакты" }
]

/* Numbers in the dark hero band */
const PROOF = [
    { value: "200+", label: "организаций и мастеров работают в Bookify", arrow: true },
    { value: "3 000+", label: "записей проходит через Bookify каждый месяц", arrow: false },
    { value: "24/7", label: "онлайн-запись работает круглосуточно", arrow: false }
]

/* Product tour — one slide per screen of the workspace, named after the sidebar menu.
   phone is optional: only part of the screens has a mobile shot so far. */
const SLIDES = [
    {
        menu: "Расписание",
        web: "web-schedule.png",
        phone: "app-schedule.png",
        title: "Расписание и записи",
        text: "Все мастера в одной сетке: занятое время, свободные окна и загрузка дня видны сразу.",
        points: ["Новая запись за два клика", "Перенос записи перетаскиванием", "Статус и канал каждой записи"]
    },
    {
        menu: "История",
        web: "web-history.png",
        phone: "app-history.png",
        title: "Статистика и журнал записей",
        text: "Выручка за период, средний чек, число записей и доля отмен. Есть сравнение с предыдущим периодом.",
        points: ["Разбивка по мастерам и услугам", "Структура выручки и способы оплаты", "Выгрузка отчёта"]
    },
    {
        menu: "Клиенты",
        web: "web-clients.png",
        phone: "app-clients.png",
        title: "Клиентская база",
        text: "Клиент сохраняется автоматически при первой записи. Видно число визитов, сумму за всё время и дату последнего визита.",
        points: ["Поиск по имени и номеру телефона", "Сегменты: VIP, постоянные, новые", "История визитов и заметки"]
    },
    {
        menu: "Специалисты",
        web: "web-team.png",
        phone: "",
        title: "Мастера и администраторы",
        text: "Сотрудники организации с разными правами доступа. Для каждого мастера задаётся процент или ставка.",
        points: ["Права на расписание, записи и финансы", "Процент мастера для расчёта зарплаты", "Приглашение по номеру телефона"]
    },
    {
        menu: "Услуги",
        web: "web-services.png",
        phone: "app-services.png",
        title: "Прайс-лист",
        text: "Услуги с ценой, длительностью и категорией. Можно указать скидку и выбрать мастеров, которые их выполняют.",
        points: ["Цена, время и категория услуги", "Скидка со старой ценой", "Архив вместо удаления"]
    },
    {
        menu: "Отзывы",
        web: "web-reviews.png",
        phone: "",
        title: "Отзывы клиентов",
        text: "Средняя оценка организации и разбивка по звёздам. На каждый отзыв можно ответить.",
        points: ["Фильтры: с ответом и без ответа", "Ответ от имени организации", "Жалоба на отзыв"]
    },
    {
        menu: "Расходы",
        web: "web-expenses.png",
        phone: "",
        title: "Расходы и прибыль",
        text: "Аренда, закуп, реклама, зарплаты и авансы по категориям. Прибыль считается как выручка минус расходы.",
        points: ["Расходы по категориям", "Выплаты и авансы мастерам", "Статус: оплачено или ожидает"]
    }
]

/* Subscription plans — the monthly price is the base, longer terms are discounted */
const PLANS = [
    { title: "Месячный", duration: "1 месяц", month: "5 000 ₸", total: "5 000 ₸", discount: "", badge: "", featured: false },
    { title: "Квартальный", duration: "3 месяца", month: "4 500 ₸", total: "13 500 ₸", discount: "−10%", badge: "", featured: false },
    { title: "Полугодовой", duration: "6 месяцев", month: "4 250 ₸", total: "25 500 ₸", discount: "−15%", badge: "Популярный", featured: true },
    { title: "Годовой", duration: "12 месяцев", month: "4 000 ₸", total: "48 000 ₸", discount: "−20%", badge: "Выгодный", featured: false }
]

/* What every plan includes */
const INCLUDED = [
    "Веб-версия и мобильное приложение",
    "Неограниченное число мастеров и записей",
    "Напоминания клиентам в WhatsApp",
    "Карточка организации в приложении Bookify",
    "Запись по QR-коду и по ссылке",
    "Клиентская база, отзывы и аналитика",
    "Учёт расходов и зарплат мастеров",
    "Помощь с настройкой и переносом данных"
]

/* Three steps from sign-up to the first online booking */
const HOW = [
    { title: "Вход по номеру", text: "Открываете pro.bookify.kz и подтверждаете телефон кодом." },
    { title: "Настройка за 15 минут", text: "Добавляете услуги с ценами, мастеров и график работы." },
    { title: "Приём записей", text: "Организация появляется в приложении Bookify, клиенты выбирают время сами." }
]

/* Short advantages under the steps */
const PERKS = [
    { icon: "percent", label: "Без комиссии с записей" },
    { icon: "user", label: "Мастеров без ограничений" },
    { icon: "whatsapp", label: "Напоминания в WhatsApp" },
    { icon: "globe", label: "Браузер и телефон" }
]

/* What the master gets on the phone */
const APP_POINTS = [
    "Записи и смена мастера",
    "Заработок и сумма к выплате",
    "Уведомления о новых записях"
]

/* Frequently asked questions */
const QUESTIONS = [
    {
        question: "Сколько стоит Bookify Pro?",
        answer: "5 000 ₸ в месяц за организацию, независимо от числа мастеров и записей. При оплате за квартал, полгода или год действует скидка до 20%. Комиссия с записей не взимается."
    },
    {
        question: "Нужно ли что-то устанавливать?",
        answer: "Нет. Веб-версия открывается в браузере на компьютере. Мобильное приложение нужно для работы с телефона — вход по тому же номеру, данные общие."
    },
    {
        question: "Как происходит вход?",
        answer: "По номеру телефона и коду подтверждения. Пароли не используются."
    },
    {
        question: "Мы ведём записи в тетради. Придётся переносить вручную?",
        answer: "Нет. Услуги, мастеров и график работы поможет перенести наш менеджер. Клиентская база дальше наполняется автоматически с каждой записи."
    },
    {
        question: "Клиенты увидят мою организацию в приложении?",
        answer: "Да. После подключения организация появляется в приложении Bookify и на bookify.kz. Запись попадает сразу в ваше расписание."
    },
    {
        question: "Что будет, если пропадёт интернет?",
        answer: "Данные хранятся на сервере, а не на устройстве. После восстановления связи расписание открывается в том же виде на любом устройстве."
    }
]

/* Contact details — icon names come from the shared Icon component */
const CONTACTS = [
    { icon: "phone", label: "Телефон", values: [{ text: "+7 (747) 312-54-58", link: "tel:+77473125458" }, { text: "+7 (707) 138-51-00", link: "tel:+77071385100" }] },
    { icon: "mail", label: "Почта", values: [{ text: "info@bookify.kz", link: "mailto:info@bookify.kz" }] },
    { icon: "globe", label: "Вход в систему", values: [{ text: "pro.bookify.kz", link: PRO_WEB }] },
    { icon: "pin", label: "Офис в Алматы", values: [{ text: "мкр. Мамыр 1, 26, БЦ Quorum, каб. 508", link: "" }] }
]

/* Page Partner */
class Partner extends Component {

    constructor(props) {
        super(props)

        /* State */
        this.state = {
            menu: false,
            slide: 0,
            indicator: { left: 0, width: 0 },
            ready: false,
            question: 0,
            name: "",
            phone: "",
            sent: false,
            loading: false,
            error: ""
        }

        /* Drawer node — used to close the menu on an outside click */
        this.drawer = null

        /* Autoplay timer of the product tour */
        this.timer = null

        /* Horizontal touch position of the running swipe */
        this.touch = null

        /* Menu strip and its buttons — measured to place the sliding indicator */
        this.strip = null
        this.items = []

        /* Deferred measurements */
        this.frame = null
        this.later = null
    }

    componentDidMount = () => {

        /* SEO */
        applySeo({
            title: "Bookify Pro — CRM и онлайн-запись для организаций и мастеров",
            description: "Bookify Pro — рабочее место организации в браузере и в телефоне: расписание, напоминания клиентам в WhatsApp, клиентская база, отзывы, расходы и прибыль. 5 000 ₸ в месяц, без комиссии с записей.",
            path: "/partnership",
            keywords: "Bookify Pro, CRM для организации, онлайн запись для мастеров, программа для барбершопа, автоматизация записей, учёт записей и расходов, pro.bookify.kz",
            jsonLd: {
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                name: "Bookify Pro",
                operatingSystem: "Web, iOS, Android",
                applicationCategory: "BusinessApplication",
                description: "Онлайн-запись, расписание, клиентская база, отзывы и учёт расходов для организаций красоты, барбершопов и сервисных бизнесов Казахстана.",
                url: PRO_WEB,
                installUrl: [APP_STORE, GOOGLE_PLAY],
                offers: { "@type": "Offer", price: "5000", priceCurrency: "KZT" }
            },
            breadcrumb: [
                { name: "Главная", url: "https://bookify.kz/" },
                { name: "Стать партнёром", url: "https://bookify.kz/partnership" }
            ]
        })

        /* Open the section from the hash (/partnership#pricing) or start from the top */
        const hash = window.location.hash.replace("#", "")
        if (hash) {
            setTimeout(() => this.scroll(hash), 100)
        }
        else {
            window.scroll(0, 0)
        }

        /* Listeners */
        window.addEventListener("scroll", this.sticky)
        document.addEventListener("click", this.outside, true)

        /* Autoplay */
        this.play()

        /* Place the menu indicator, then allow it to animate from the next frame on */
        this.measure()
        this.frame = window.requestAnimationFrame(() => this.setState({ ready: true }))

        /* Web fonts change the button widths, so measure once more when they land */
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.measure())
        }
        this.later = setTimeout(this.measure, 400)

        window.addEventListener("resize", this.measure)
    }

    componentDidUpdate = (previousProps, previousState) => {

        /* Move the indicator only when the active screen actually changed */
        if (previousState.slide !== this.state.slide) {
            this.measure()
            this.reveal()
        }
    }

    componentWillUnmount = () => {

        /* Listeners */
        window.removeEventListener("scroll", this.sticky)
        window.removeEventListener("resize", this.measure)
        document.removeEventListener("click", this.outside, true)

        /* Autoplay */
        clearInterval(this.timer)

        /* Deferred measurements */
        window.cancelAnimationFrame(this.frame)
        clearTimeout(this.later)
        this.strip = null
    }

    /* Put the indicator under the active menu button */
    measure = () => {

        /* Fields */
        const { slide } = this.state

        /* The strip is nulled on unmount, so a late callback finds nothing to measure */
        const item = this.items[slide]
        if (!this.strip || !item) {
            return
        }

        this.setState({ indicator: { left: item.offsetLeft, width: item.offsetWidth } })
    }

    /* Scroll the strip so the active button stays visible on narrow screens */
    reveal = () => {

        /* Fields */
        const { slide } = this.state

        /* Nodes */
        const item = this.items[slide]
        if (!this.strip || !item) {
            return
        }

        /* Bounds */
        const left = item.offsetLeft
        const right = left + item.offsetWidth

        if (left < this.strip.scrollLeft) {
            this.strip.scrollTo({ left: left - 12, behavior: "smooth" })
        }
        else if (right > this.strip.scrollLeft + this.strip.clientWidth) {
            this.strip.scrollTo({ left: right - this.strip.clientWidth + 12, behavior: "smooth" })
        }
    }

    /* Sticky header background after the first screen */
    sticky = () => {

        /* Header */
        const header = document.querySelector(".pr-header")
        if (!header) {
            return
        }

        if (window.scrollY >= 40) {
            header.classList.add("is-sticky")
        }
        else {
            header.classList.remove("is-sticky")
        }
    }

    /* Close the drawer when the click lands outside of it */
    outside = (event) => {

        /* Fields */
        const { menu } = this.state

        if (menu && this.drawer && !this.drawer.contains(event.target)) {
            this.setState({ menu: false })
        }
    }

    /* Scroll to a section by its anchor id */
    scroll = (id) => {

        /* Top */
        if (!id || id === "top") {
            window.scrollTo({ top: 0, behavior: "smooth" })
            this.setState({ menu: false })
            return
        }

        /* Section */
        const target = document.getElementById(id)
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" })
        }

        this.setState({ menu: false })
    }

    /* Start the tour autoplay */
    play = () => {
        clearInterval(this.timer)
        this.timer = setInterval(() => this.move(1), 6000)
    }

    /* Stop the tour autoplay — any manual interaction wins over it */
    stop = () => {
        clearInterval(this.timer)
        this.timer = null
    }

    /* Shift the tour by one slide, wrapping around both ends */
    move = (step) => {

        /* Fields */
        const { slide } = this.state

        /* Total */
        const total = SLIDES.length
        const next = (slide + step + total) % total

        this.setState({ slide: next })
    }

    /* Jump to an exact slide from the dots */
    jump = (index) => {
        this.stop()
        this.setState({ slide: index })
    }

    /* Step the tour with the arrows */
    step = (value) => {
        this.stop()
        this.move(value)
    }

    /* Remember where the swipe started */
    start = (event) => {
        this.touch = event.touches[0].clientX
    }

    /* Turn a finished swipe into a slide change */
    end = (event) => {

        /* Distance */
        if (this.touch === null) {
            return
        }
        const shift = event.changedTouches[0].clientX - this.touch
        this.touch = null

        /* Ignore taps and vertical scrolls */
        if (Math.abs(shift) < 50) {
            return
        }

        this.stop()
        if (shift < 0) {
            this.move(1)
        }
        else {
            this.move(-1)
        }
    }

    /* Format the phone as +7 (707) 138-51-00 while typing */
    format = (event) => {

        /* Digits without the country code */
        let digits = event.target.value.replace(/\D/g, "")
        if (digits.startsWith("7") || digits.startsWith("8")) {
            digits = digits.slice(1)
        }
        digits = digits.slice(0, 10)

        /* Mask */
        let value = "+7"
        if (digits.length > 0) {
            value = `${value} (${digits.slice(0, 3)}`
        }
        if (digits.length >= 3) {
            value = `${value}) ${digits.slice(3, 6)}`
        }
        if (digits.length >= 6) {
            value = `${value}-${digits.slice(6, 8)}`
        }
        if (digits.length >= 8) {
            value = `${value}-${digits.slice(8, 10)}`
        }

        this.setState({ phone: value, error: "" })
    }

    /* Send the callback request to the Telegram chat of the team */
    submit = (event) => {

        /* Fields */
        const { name, phone, loading } = this.state

        event.preventDefault()

        /* Guard */
        if (loading) {
            return
        }

        /* Validate */
        if (name.trim().length < 2) {
            this.setState({ error: "Укажите имя" })
            return
        }
        if (phone.replace(/\D/g, "").length < 11) {
            this.setState({ error: "Укажите номер телефона" })
            return
        }

        /* Send */
        this.setState({ loading: true, error: "" })
        partner({ name: name.trim(), phone, source: "partnership" }).then((response) => {

            /* Sent */
            if (response && (response.status === 201 || response.status === 200)) {
                this.setState({ sent: true, name: "", phone: "", error: "" })
                return
            }

            /* Answer of the server */
            const fields = response && response.data && response.data.error ? response.data.error.fields : null
            if (fields && (fields.name || fields.phone)) {
                this.setState({ error: fields.name ? fields.name : fields.phone })
                return
            }

            /* Failure */
            this.setState({ error: "Не удалось отправить заявку. Напишите нам в WhatsApp" })
        }).catch(() => {
            this.setState({ error: "Не удалось отправить заявку. Напишите нам в WhatsApp" })
        }).finally(() => {
            this.setState({ loading: false })
        })
    }

    /* Toggle a question in the FAQ accordion */
    toggle = (index) => {

        /* Fields */
        const { question } = this.state

        if (question === index) {
            this.setState({ question: -1 })
        }
        else {
            this.setState({ question: index })
        }
    }

    /* Draw Header */
    _header = () => {
        return (
            <header className="pr-header">
                <div className="pr-header__inner">

                    <div className="pr-header__logo" onClick={() => this.scroll("top")}>
                        <img src="/partner/logo.png" alt="Bookify Pro" />
                    </div>

                    <nav className="pr-header__nav">
                        {NAVIGATION.map((item, index) => (
                            <button type="button" key={`${index}`} className="pr-header__link" onClick={() => this.scroll(item.id)}>{item.label}</button>
                        ))}
                    </nav>

                    <div className="pr-header__actions">
                        <a href={PRO_WEB} target="_blank" rel="noreferrer" className="pr-btn pr-btn--dark">Войти</a>
                    </div>

                    <button type="button" className="pr-header__burger" onClick={() => this.setState({ menu: true })} aria-label="Меню">
                        <img src="/partner/menu.png" alt="Меню" />
                    </button>

                </div>
            </header>
        )
    }

    /* Draw Drawer */
    _drawer = () => {

        /* Fields */
        const { menu } = this.state

        return (
            <div ref={(ref) => this.drawer = ref} className={`pr-drawer ${menu ? "is-open" : ""}`}>

                <div className="pr-drawer__head">
                    <img className="pr-drawer__logo" src="/partner/logo.png" alt="Bookify Pro" />
                    <button type="button" className="pr-drawer__close" onClick={() => this.setState({ menu: false })} aria-label="Закрыть">
                        <img src="/partner/close.png" alt="Закрыть" />
                    </button>
                </div>

                {NAVIGATION.map((item, index) => (
                    <button type="button" key={`${index}`} className="pr-drawer__item" onClick={() => this.scroll(item.id)}>{item.label}</button>
                ))}

                <a href={PRO_WEB} target="_blank" rel="noreferrer" className="pr-btn pr-btn--solid pr-btn--wide">Начать</a>

            </div>
        )
    }

    /* Draw Hero */
    _hero = () => {
        return (
            <section className="pr-hero">

                <div className="pr-hero__top pr-inner">
                    <div className="pr-hero__info">

                        <div className="pr-hero__badge">
                            <span className="pr-hero__badge-dot" />
                            CRM для бизнеса
                        </div>

                        <h1 className="pr-hero__title">
                            Автоматизируй бизнес.<br />
                            Управляй расписанием.<br />
                            Расти{" "}
                            <span className="pr-hero__mark">
                                <span className="pr-hero__mark-text">быстрее</span>
                                <svg className="pr-hero__ellipse" viewBox="0 0 300 100" preserveAspectRatio="none" aria-hidden="true">
                                    <path d="M236 24C198 9 126 3 74 16 22 29 3 53 21 71c18 18 90 27 152 21 62-6 116-28 119-48 3-19-28-30-64-32" pathLength="1" />
                                </svg>
                            </span>.
                            <svg className="pr-hero__star" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M12 1.4c.86 6.26 3.48 8.88 9.74 9.74-6.26.86-8.88 3.48-9.74 9.74-.86-6.26-3.48-8.88-9.74-9.74C8.52 10.28 11.14 7.66 12 1.4z" />
                            </svg>
                        </h1>

                        <p className="pr-hero__lead">
                            Простое решение для мастеров и администраторов, чтобы упростить
                            работу и увеличить доход.
                        </p>

                        <div className="pr-hero__actions">
                            <a href={PRO_WEB} target="_blank" rel="noreferrer" className="pr-btn pr-btn--solid pr-btn--big">
                                <span>Начать</span>
                                <Icon name="arrow_right" size={18} />
                            </a>
                        </div>

                    </div>

                    <div className="pr-hero__devices">
                        <img className="pr-hero__laptop" src="/partner/shots/web-schedule.png" alt="Расписание Bookify Pro на компьютере" />
                        <img className="pr-hero__phone" src="/partner/shots/app-schedule.png" alt="Расписание Bookify Pro в телефоне" />
                    </div>

                </div>

                <div className="pr-hero__bottom">
                    <div className="pr-hero__glow" />
                    <div className="pr-inner">
                        <div className="pr-hero__stats">
                            {PROOF.map((item, index) => (
                                <div className="pr-stat" key={`${index}`}>
                                    <div className="pr-stat__value">
                                        {item.arrow ? (
                                            <svg className="pr-stat__arrow" viewBox="0 0 24 24" aria-hidden="true">
                                                <path d="M12 21V4M5 11l7-7 7 7" />
                                            </svg>
                                        ) : null}
                                        <span>{item.value}</span>
                                    </div>
                                    <div className="pr-stat__label">{item.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

            </section>
        )
    }

    /* Draw Tour */
    _tour = () => {

        /* Fields */
        const { slide, indicator, ready } = this.state

        /* Active screen */
        const active = SLIDES[slide]

        return (
            <section id="features" className="pr-section pr-tour">
                <div className="pr-inner">

                    <div className="pr-head">
                        <h2 className="pr-title">Возможности</h2>
                        <p className="pr-lead">Что получает ваш бизнес с Bookify Pro</p>
                    </div>

                    <div ref={(ref) => this.strip = ref} className="pr-tour__menu">
                        <span className={`pr-tour__pill ${ready ? "is-ready" : ""}`} style={{ transform: `translateX(${indicator.left}px)`, width: `${indicator.width}px` }} />
                        {SLIDES.map((item, index) => (
                            <button type="button" key={`${index}`} ref={(ref) => this.items[index] = ref} className={`pr-tour__menu-item ${slide === index ? "is-active" : ""}`} onClick={() => this.jump(index)}>{item.menu}</button>
                        ))}
                    </div>

                    <div className="pr-tour__stage">

                        <div className="pr-tour__panel">
                            <span className="pr-tour__rule" />
                            <h3 className="pr-tour__title">{active.title}</h3>
                            <p className="pr-tour__text">{active.text}</p>

                            <div className="pr-tour__points">
                                {active.points.map((point, index) => (
                                    <div className="pr-tour__point" key={`${index}`}>
                                        <span className="pr-tour__check">
                                            <Icon name="check" size={13} />
                                        </span>
                                        <span>{point}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="pr-tour__controls">
                                <button type="button" className="pr-tour__arrow" onClick={() => this.step(-1)} aria-label="Предыдущий экран">
                                    <Icon name="chevron_left" size={18} />
                                </button>
                                <button type="button" className="pr-tour__arrow" onClick={() => this.step(1)} aria-label="Следующий экран">
                                    <Icon name="chevron_right" size={18} />
                                </button>
                                <div className="pr-tour__counter">{`${slide + 1} / ${SLIDES.length}`}</div>
                            </div>
                        </div>

                        <div className="pr-tour__viewport" onTouchStart={this.start} onTouchEnd={this.end}>
                            <div className="pr-tour__track" style={{ transform: `translateX(-${slide * 100}%)` }}>
                                {SLIDES.map((item, index) => (
                                    <div className="pr-tour__slide" key={`${index}`}>
                                        <div className="pr-tour__media">
                                            <img className="pr-tour__laptop" src={`/partner/shots/${item.web}`} alt={`${item.menu} — Bookify Pro на компьютере`} loading={index === 0 ? "eager" : "lazy"} />
                                            {item.phone ? (
                                                <img className="pr-tour__phone" src={`/partner/shots/${item.phone}`} alt={`${item.menu} — Bookify Pro в телефоне`} loading="lazy" />
                                            ) : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        )
    }

    /* Draw Pricing */
    _pricing = () => {
        return (
            <section id="pricing" className="pr-section pr-pricing">
                <div className="pr-inner">

                    <div className="pr-head">
                        <div className="pr-eyebrow">Тарифы</div>
                        <h2 className="pr-title">Одна подписка на всю организацию</h2>
                        <p className="pr-lead">Число мастеров, записей и клиентов не ограничено. Комиссия с записей не взимается.</p>
                    </div>

                    <div className="pr-pricing__grid">
                        {PLANS.map((plan, index) => (
                            <div className={`pr-plan ${plan.featured ? "is-featured" : ""}`} key={`${index}`}>
                                {plan.badge ? <div className="pr-plan__badge">{plan.badge}</div> : null}
                                <div className="pr-plan__title">{plan.title}</div>
                                <div className="pr-plan__duration">{plan.duration}</div>
                                <div className="pr-plan__price">
                                    {plan.month}
                                    <span className="pr-plan__unit">/мес</span>
                                </div>
                                <div className="pr-plan__total">
                                    <span>{`${plan.total} за период`}</span>
                                    {plan.discount ? <em className="pr-plan__discount">{plan.discount}</em> : null}
                                </div>
                                <a href={PRO_WEB} target="_blank" rel="noreferrer" className="pr-btn pr-btn--outline pr-btn--wide">Выбрать</a>
                            </div>
                        ))}
                    </div>

                    <div className="pr-included">
                        <div className="pr-included__head">
                            <div className="pr-included__title">Входит в любой тариф</div>
                            <p className="pr-included__text">Отдельных доплат за мастеров, уведомления и отчёты нет.</p>
                        </div>
                        <div className="pr-included__grid">
                            {INCLUDED.map((item, index) => (
                                <div className="pr-included__item" key={`${index}`}>
                                    <Icon name="check" size={15} />
                                    <span>{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </section>
        )
    }

    /* Draw How */
    _how = () => {
        return (
            <section id="how" className="pr-how">
                <div className="pr-inner">

                    <div className="pr-head pr-how__head">
                        <h2 className="pr-title">Как это работает</h2>
                        <p className="pr-lead">От регистрации до первой онлайн-записи — один вечер.</p>
                    </div>

                    <div className="pr-how__steps">
                        {HOW.map((step, index) => (
                            <div className="pr-how__step" key={`${index}`}>
                                <div className="pr-how__index">{index + 1}</div>
                                <h3 className="pr-how__title">{step.title}</h3>
                                <p className="pr-how__text">{step.text}</p>
                            </div>
                        ))}
                    </div>

                    <div className="pr-how__perks">
                        {PERKS.map((perk, index) => (
                            <div className="pr-how__perk" key={`${index}`}>
                                <Icon name={perk.icon} size={17} />
                                <span>{perk.label}</span>
                            </div>
                        ))}
                    </div>

                </div>
            </section>
        )
    }

    /* Draw Questions */
    _questions = () => {

        /* Fields */
        const { question } = this.state

        return (
            <section id="faq" className="pr-section pr-faq">
                <div className="pr-inner">

                    <div className="pr-faq__head">
                        <h2 className="pr-title">Частые вопросы</h2>
                        <p className="pr-lead">Если вашего вопроса здесь нет — оставьте заявку, ответим в рабочее время.</p>
                    </div>

                    <div className="pr-faq__list">
                        {QUESTIONS.map((item, index) => (
                            <div className={`pr-faq__item ${question === index ? "is-open" : ""}`} key={`${index}`}>
                                <button type="button" className="pr-faq__question" onClick={() => this.toggle(index)}>
                                    <span>{item.question}</span>
                                    <span className="pr-faq__icon">
                                        <Icon name={question === index ? "minus" : "plus"} size={16} />
                                    </span>
                                </button>
                                <div className="pr-faq__answer">
                                    <p>{item.answer}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section>
        )
    }

    /* Draw Mobile */
    _mobile = () => {
        return (
            <section id="app" className="pr-mobile">
                <div className="pr-mobile__glow" />
                <div className="pr-inner pr-mobile__inner">

                    <div className="pr-mobile__info">

                        <div className="pr-mobile__eyebrow">Мобильное приложение</div>
                        <h2 className="pr-mobile__title">Bookify Pro в телефоне</h2>
                        <p className="pr-mobile__text">
                            Мастер видит свой день, отмечает визиты и звонит клиенту из приложения.
                            Вход по тому же номеру — данные общие с веб-версией.
                        </p>

                        <div className="pr-mobile__points">
                            {APP_POINTS.map((point, index) => (
                                <div className="pr-mobile__point" key={`${index}`}>
                                    <Icon name="check" size={15} />
                                    <span>{point}</span>
                                </div>
                            ))}
                        </div>

                        <div className="pr-stores">
                            <a href={APP_STORE} target="_blank" rel="noreferrer" className="pr-store">
                                <img src="/partner/apple.png" alt="App Store" />
                                <div className="pr-store__text">
                                    <span>Доступно в</span>
                                    <strong>App Store</strong>
                                </div>
                            </a>
                            <a href={GOOGLE_PLAY} target="_blank" rel="noreferrer" className="pr-store">
                                <img src="/partner/google-play.png" alt="Google Play" />
                                <div className="pr-store__text">
                                    <span>Загрузите на</span>
                                    <strong>Google Play</strong>
                                </div>
                            </a>
                        </div>

                        <div className="pr-mobile__qr">
                            <QRCodeSVG value={INSTALL} size={78} level="M" bgColor="#ffffff" fgColor="#080c16" />
                            <div className="pr-mobile__qr-text">Наведите камеру телефона — откроется установка для вашей системы</div>
                        </div>

                    </div>

                    <div className="pr-mobile__device">
                        <img src="/partner/shots/app-banner.png" alt="Приложение Bookify Pro: приём и подтверждение записей" loading="lazy" />
                    </div>

                </div>
            </section>
        )
    }

    /* Draw Form */
    _form = () => {

        /* Fields */
        const { name, phone, sent, loading, error } = this.state

        /* Sent */
        if (sent) {
            return (
                <div className="pr-form__success">
                    <div className="pr-form__success-icon">
                        <Icon name="check" size={22} />
                    </div>
                    <div className="pr-form__success-title">Спасибо, заявку получили</div>
                    <p className="pr-form__success-text">Свяжемся с вами в рабочее время.</p>
                    <a href={PRO_WEB} target="_blank" rel="noreferrer" className="pr-btn pr-btn--outline pr-btn--wide">Открыть pro.bookify.kz</a>
                </div>
            )
        }

        /* Form */
        return (
            <form className="pr-form__fields" onSubmit={this.submit}>
                <input className="pr-input" type="text" placeholder="Ваше имя" value={name} onChange={(event) => this.setState({ name: event.target.value, error: "" })} />
                <input className="pr-input" type="tel" placeholder="+7 (___) ___-__-__" value={phone} onChange={this.format} />
                {error ? <div className="pr-form__error">{error}</div> : null}
                <button type="submit" className="pr-btn pr-btn--solid pr-btn--wide pr-btn--big" disabled={loading}>{loading ? "Отправляем…" : "Перезвоните мне"}</button>
                <p className="pr-form__consent">Продолжая, вы соглашаетесь с <a href="/partner/privacy-policy" target="_blank" rel="noreferrer">политикой конфиденциальности</a></p>
            </form>
        )
    }

    /* Draw Contacts */
    _contacts = () => {
        return (
            <section id="contacts" className="pr-section pr-contacts">
                <div className="pr-inner">

                    <div className="pr-head">
                        <h2 className="pr-title">Поможем начать</h2>
                        <p className="pr-lead">Позвоните или оставьте номер — перезвоним, покажем систему на ваших услугах и поможем перенести данные.</p>
                    </div>

                    <div className="pr-contacts__body">

                        <div className="pr-form">
                            <div className="pr-form__title">Оставьте заявку</div>
                            <p className="pr-form__text">Перезвоним и ответим на вопросы</p>
                            {this._form()}
                        </div>

                        <div className="pr-contacts__list">
                            {CONTACTS.map((contact, index) => (
                                <div className="pr-contact" key={`${index}`}>
                                    <span className="pr-contact__icon">
                                        <Icon name={contact.icon} size={18} />
                                    </span>
                                    <div className="pr-contact__body">
                                        <div className="pr-contact__label">{contact.label}</div>
                                        {contact.values.map((value, position) => {

                                            /* Plain text — the office address has nothing to open */
                                            if (!value.link) {
                                                return <div className="pr-contact__value" key={`${position}`}>{value.text}</div>
                                            }

                                            return <a className="pr-contact__value" href={value.link} key={`${position}`} target={value.link.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{value.text}</a>
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>

                </div>
            </section>
        )
    }

    /* Draw Footer */
    _footer = () => {
        return (
            <footer className="pr-footer">
                <div className="pr-inner pr-footer__inner">

                    <div className="pr-footer__brand">
                        <img className="pr-footer__logo" src="/partner/logo-white.png" alt="Bookify Pro" />
                        <p className="pr-footer__text">Расписание, клиенты, отзывы и финансы организации — в браузере и в телефоне.</p>
                        <a href={PRO_WEB} target="_blank" rel="noreferrer" className="pr-footer__address">pro.bookify.kz</a>
                    </div>

                    <div className="pr-footer__column">
                        <div className="pr-footer__title">Продукт</div>
                        <button type="button" onClick={() => this.scroll("features")}>Возможности</button>
                        <button type="button" onClick={() => this.scroll("pricing")}>Тарифы</button>
                        <button type="button" onClick={() => this.scroll("faq")}>Вопросы</button>
                    </div>

                    <div className="pr-footer__column">
                        <div className="pr-footer__title">Документы</div>
                        <a href="/partner/privacy-policy" target="_blank" rel="noreferrer">Политика конфиденциальности</a>
                        <a href="/partner/public-offer" target="_blank" rel="noreferrer">Публичная оферта</a>
                        <a href="https://bookify.kz" target="_blank" rel="noreferrer">Bookify для клиентов</a>
                    </div>

                    <div className="pr-footer__column">
                        <div className="pr-footer__title">Компания</div>
                        <div className="pr-footer__static">ТОО «SARY GROUP»</div>
                        <div className="pr-footer__static">БИН 240440023071</div>
                        <a href="tel:+77071385100">+7 (747) 312-54-58</a>
                        <a href="mailto:info@bookify.kz">info@bookify.kz</a>
                        <div className="pr-footer__social">
                            <a href="https://instagram.com/bookify.kz" target="_blank" rel="noreferrer" aria-label="Instagram">
                                <img src="/partner/instagram.png" alt="Instagram" />
                            </a>
                        </div>
                    </div>

                </div>

                <div className="pr-footer__bottom">
                    <div className="pr-inner">© 2026 ТОО «SARY GROUP». Все права защищены.</div>
                </div>
            </footer>
        )
    }

    /* Draw Whatsapp */
    _whatsapp = () => {
        return (
            <a href={WHATSAPP} target="_blank" rel="noreferrer" className="pr-whatsapp" aria-label="Написать в WhatsApp">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
            </a>
        )
    }

    /* Render */
    render = () => {
        return (
            <div className="pr">
                {this._header()}
                {this._drawer()}
                {this._hero()}
                {this._tour()}
                {this._pricing()}
                {this._how()}
                {this._questions()}
                {this._mobile()}
                {this._contacts()}
                {this._footer()}
                {this._whatsapp()}
            </div>
        )
    }
}

/* Export */
export default Partner
